import {Effect} from '..';
import {AppActions} from './';
import { AuthActions, AuthEffects } from "../auth";
import {buildBaseHeader, getBaseUrl, setRedirectPath} from "../../util/helper-methods";
import {defaultMerchantRedirect, defaultPersonalRedirect, defaultTestingRedirect} from "../../util/constants";
import axios from "axios";
import {Context} from "../../util/interfaces";
import {push} from "connected-react-router";
import {Routes} from "../../routes";
import {AccountEffects} from "../account";
import { hashPassword } from "../../util/password";

export const init = (): Effect =>
   async (dispatch, getState) => {
    await dispatch(AppActions.parseParams(window.location.href))
    await dispatch(AuthEffects.startGetSession());


    const { auth, app } = getState();
    if (window.Cypress) {
      if (auth.session?.isAuthenticated && window.location.pathname === 'success-test' ) {
        window.location.assign(defaultTestingRedirect);
      }
    } else {
      // applying email verify here because it must be applied whether authenticated or not
      if (app.instructions.apply === 'emailVerify' && app.queryParams.payload) {
        await dispatch(AppActions.removeParams(['payload'])); // pull payload param off so that this flow doesn't get entered again.
        dispatch(AuthEffects.verifyEmail(app.queryParams.payload));
      } else if (app.instructions.apply === 'emailAuth' && (app.queryParams.allowToken || app.queryParams.rejectToken)) {
        if(app.queryParams.rejectToken) {
          await dispatch(AuthActions.setRejectToken(app.queryParams.rejectToken));
          await dispatch(AppActions.removeParams(['rejectToken']));
          dispatch(AuthEffects.rejectEmail2fa(app.queryParams.rejectToken));
        } else {
          await dispatch(AuthActions.setAllowToken(app.queryParams.allowToken));
          await dispatch(AppActions.removeParams(['allowToken']));
          dispatch(AuthEffects.verifyEmail2fa(app.queryParams.allowToken));
        }
      } else if(auth.session?.isAuthenticated && !['finish-signup', 'email-verification', 'alert'].some((route) => window.location.pathname.includes(route))) {
        await dispatch(handleCompletion());
      }
    }
     await dispatch(AppActions.initComplete());
  };

/**
 * The program is expecting to receive redirect instruction on the 'context' parameter. The format of that parameter is
 * {
        url: "invoice", //NOTICE THAT THERE IS NO '/' BEFORE THE REDIRECT PATH
        queryParams: {
            v: 3,
            id: "HeK5oAXkVxRjSgzwrjB1GY",
            lang:"en-US",
        },
        isWindow: true,
    }
 *
 * The apply parameter is used to determine is a method should be applied before redirecting, such as claiming an invoice
 * or starting bitpayId verification should occur.
 */

export const handleCompletion = (): Effect =>
    async (dispatch, getState) => {
        const { app, auth, account } = getState();
        const {context, apply} = app.instructions;
        let useContext = true;
        const isMerchant = auth.session?.accessTypes && !!auth.session.accessTypes.includes('merchant')

        // handle illegal attempt to redirect non-merchant user to merchant dashboard
        if(context?.url && context?.url.startsWith('dashboard') && !isMerchant) {
          useContext = false;
        }

      if(apply){
        //if a product needs to be applied
        switch (apply) {
          case 'verify':
            // todo add verify
            break;
          case 'shopper1':
            if (!app.isInvoiceClaimed) {
              if (context?.queryParams && context.queryParams.id) {
                dispatch(startClaimInvoice(context.queryParams.id, context.queryParams.purchaserId))
              } else {
                dispatch(AppActions.failedClaimInvoice('Invoice ID not provided'));
                dispatch(AppActions.setIsLoading(false));
              }
            } else {
              dispatch(completionRedirect(context, isMerchant));
            }
            break;
          case 'shopify':
            //check if its a merchant
            if(isMerchant && !account.isShopifyAdded && !account.accountError && app.queryParams.shop) {
              await dispatch(AccountEffects.getAccount());
              await dispatch(AccountEffects.addShopifyAttributes());
              dispatch(handleCompletion());
            } else if (!account.accountError) {
              dispatch(completionRedirect(useContext ? context : undefined, isMerchant));
            }
            break;
          case 'ada':
            dispatch(AuthEffects.applyAdaToken());
            break;
          default:
            dispatch(completionRedirect(useContext ? context : undefined, isMerchant));
            break;
        }
        } else {
          dispatch(completionRedirect(useContext ? context : undefined, isMerchant));
        }
    };

export const startClaimInvoice = (invoiceId:string, purchaserKey:string): Effect =>
  async (dispatch, getState) => {
    try {
      const headers = {...buildBaseHeader(getState())};
      await axios.post('/invoiceData/unlockShopperInvoice', { invoiceId, purchaserKey }, {headers});
      await dispatch(AppActions.successClaimInvoice());
      await dispatch(handleCompletion())
    } catch (err) {
      // todo pass the error message from the response object to the action
      await dispatch(AppActions.failedClaimInvoice('An unexpected error occurred'))
    }
  };

/**
 * Redirect the user to the proper endpoint after completion of signup or login
 * @param context
 * @param isMerchant
 */
export const completionRedirect = (context: Context | undefined, isMerchant: boolean): Effect =>
  async (dispatch, getState) => {

    const {app, auth, account} = getState();

    if (window.Cypress) {
      // if we are in a test environment redirect to the success-test page
      dispatch(AppActions.setIsLoading(false));
      window.location.assign(defaultTestingRedirect);
    } else if (!isMerchant && !auth.session.verified) {
      dispatch(AuthEffects.startVerifyEmail());
      dispatch(push(Routes.LOGIN + app.urlExtension));
      dispatch(AppActions.setIsLoading(false));
    } else {
      if (context && Object.keys(context).length && app.queryParams.dest === 'poynt') {
        // if this is a poynt signup
        if (isMerchant) {
          // if for some reason this user already has a merchant account, send them to poynt in dashboard
          window.location.assign(getBaseUrl() + `/dashboard/payment-tools/poynt`)
        } else {
          // if the user is not yet a merchant redirect them to the poynt website (pass that this is a redirect to an external site)
          window.location.assign(setRedirectPath(context, true));
        }

      } else if (app.queryParams.dest === 'shopify') {
        // if this is a shopify signup
        if (!isMerchant) {
          // if the user only has a personal account, send them to finish signup
          window.location.assign('/authenticate/finish-signup' + app.urlExtension);
        } else {
          window.location.assign(`/dashboard/getstarted`);
        }
      } else if (context && context?.isWindow) {
        // if this is happening within a window utilize a post message
        window.opener.postMessage(context?.queryParams && context.queryParams?.id, '*');
      } else if (context && Object.keys(context).length) {
        // if this is not happening in a window build the redirect path with the util
        window.location.assign(setRedirectPath(context));
      } else if (isMerchant) {
        // if there is no context object and it is a merchant redirect to dashboard
        window.location.assign(defaultMerchantRedirect);
      } else {
        // if there is no context object and it is not a merchant redirect to personal dashboard
        window.location.assign(defaultPersonalRedirect);
      }
    }
  }

export const redirect = (destination: string): Effect =>
  async (dispatch) => {
    window.location.assign(destination);
  }

export const getIndustries = (): Effect =>
  async (dispatch, getState) => {
    const { app } = getState();
    if (app && app.industries.length === 0) {
      try {
        const { data } = await axios.get('/industries');
        dispatch(AppActions.setIndustries(data.data));
      } catch (e) {
        console.log('error retrieving industries');
      }
    }
  }

export const getCountries = (): Effect =>
  async (dispatch, getState) => {
    const { app } = getState();
    if (app && app.countries.length === 0) {
      try {
        const { data } = await axios.get('/countries');
        dispatch(AppActions.setCountries(data.data));
      } catch (e) {
        console.log('error retrieving countries');
      }
    }
  }
